/**
 * Module dependencies.
 */

import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loading } from 'app/components/atoms/loading/loading';
import { Title } from 'app/components/atoms/title/title';
import { Field } from 'app/components/organisms/field/field';
import { useProject } from 'app/hooks/requests/projects/use-project';
import { useProjectOverview } from 'app/hooks/requests/projects/use-project-overview';
import { formatCurrency } from 'app/core/utils/formatter';
import { resolveAppPath } from 'app/core/utils/url-resolver';
import { appRoutes } from 'app/routes';
import { cn } from 'app/lib/utils';
import BigNumber from 'bignumber.js';
import moment from 'moment';
import { AlertTriangle, CheckCircle2, Layers, TrendingUp } from 'lucide-react';
import { NoteLinksPanel } from 'app/components/pages/notes/note-links-panel';

/**
 * `StatCard` component.
 */

function StatCard({
  icon,
  label,
  value,
  tone
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  tone?: string;
}): JSX.Element {
  return (
    <div className={'rounded-xl border border-border bg-card p-5 shadow-sm'}>
      <div className={'flex items-center justify-between gap-3'}>
        <span className={'text-xs font-medium text-muted-foreground'}>{label}</span>

        <span
          className={
            'grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-muted text-muted-foreground [&_svg]:h-[18px] [&_svg]:w-[18px]'
          }
        >
          {icon}
        </span>
      </div>

      <div className={cn('mt-3 text-[26px] font-semibold leading-none tabular-nums text-foreground', tone)}>
        {value}
      </div>
    </div>
  );
}

/**
 * `Panel` component.
 */

function Panel({ title, children }: { title: string; children: React.ReactNode }): JSX.Element {
  return (
    <div className={'rounded-xl border border-border bg-card shadow-sm'}>
      <div className={'border-b border-border px-5 py-3.5 text-sm font-semibold text-foreground'}>{title}</div>

      <div className={'p-5'}>{children}</div>
    </div>
  );
}

/**
 * `DetailGroup` component — a titled/described column of `Field` rows.
 */

function DetailGroup({
  title,
  description,
  children
}: {
  title: string;
  description: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div>
      <div className={'border-b-2 border-border pb-2 text-sm font-bold uppercase tracking-wide text-primary'}>
        {title}
      </div>

      <p className={'mb-3 mt-2 text-xs text-muted-foreground'}>{description}</p>

      {children}
    </div>
  );
}

/**
 * Export `Project` component.
 */

export function Project(): JSX.Element | null {
  const { projectId } = useParams();
  const [translate] = useTranslation();
  const navigate = useNavigate();
  const { data: project, isLoading } = useProject(projectId);
  const { data: overview } = useProjectOverview(projectId);

  const goToBudgets = () => navigate(resolveAppPath(appRoutes.dashboard.projects.project.budgets, { projectId }));
  const goToMovements = () => navigate(resolveAppPath(appRoutes.dashboard.projects.project.movements, { projectId }));

  const area = project?.areaM2 ? new BigNumber(project.areaM2) : null;
  const budgets = overview?.budgets;

  return (
    <Loading isLoading={isLoading}>
      {!!project && (
        <div className={'space-y-5'}>
          <Title>{project.name}</Title>

          {!!budgets && !!overview && (
            <>
              {/* Headline */}
              <div className={'grid grid-cols-4 gap-4 max-[991px]:grid-cols-2 max-[600px]:grid-cols-1'}>
                <StatCard
                  icon={<Layers />}
                  label={translate('common.labels.initialPrevision')}
                  value={formatCurrency(budgets.initialPrevision)}
                />

                <StatCard
                  icon={<TrendingUp />}
                  label={translate('common.labels.prevision')}
                  value={formatCurrency(budgets.currentPrevision)}
                />

                <StatCard
                  icon={<CheckCircle2 />}
                  label={translate('common.labels.totalPaid')}
                  tone={'text-primary'}
                  value={formatCurrency(budgets.paid)}
                />

                <StatCard
                  icon={<AlertTriangle />}
                  label={translate('common.labels.needPaid')}
                  tone={budgets.debt.isGreaterThan(0) ? 'text-destructive' : undefined}
                  value={formatCurrency(budgets.debt)}
                />
              </div>

              {/* Detailed breakdown with descriptions */}
              <Panel title={translate('projects.projectOverview.budgetDetail')}>
                <div className={'grid grid-cols-3 gap-8 max-[991px]:grid-cols-1'}>
                  <DetailGroup
                    description={
                      'Diferença entre a previsão inicial e as propostas recebidas. Quando não há propostas, assume-se o valor inicial.'
                    }
                    title={translate('projects.projectOverview.forecast')}
                  >
                    <Field
                      info={'Soma da previsão inicial de todos os orçamentos.'}
                      title={translate('common.labels.initialPrevision')}
                      value={formatCurrency(budgets.initialPrevision)}
                    />

                    <Field
                      info={'Propostas fechadas + previsão inicial dos orçamentos ainda sem proposta.'}
                      title={translate('common.labels.prevision')}
                      value={formatCurrency(budgets.currentPrevision)}
                    />

                    <Field
                      info={'Diferença entre a previsão inicial e a atual.'}
                      title={translate('common.labels.budgetDifference')}
                      value={formatCurrency(budgets.budgetDiff)}
                    />
                  </DetailGroup>

                  <DetailGroup
                    description={'Valores já fechados (com proposta) vs. valores ainda em aberto.'}
                    title={translate('projects.projectOverview.budget')}
                  >
                    <Field
                      info={'Soma de todas as propostas fechadas (valor final já com IVA previsto).'}
                      title={translate('projects.projectOverview.closed')}
                      value={formatCurrency(budgets.closed)}
                    />

                    <Field
                      info={'Soma da previsão dos orçamentos com propostas fechadas.'}
                      title={translate('projects.projectOverview.closedBudget')}
                      value={formatCurrency(budgets.initialClosed)}
                    />

                    <Field
                      info={'Diferença entre o budget inicial e as propostas fechadas.'}
                      title={translate('projects.projectOverview.closedDiff')}
                      value={formatCurrency(budgets.closedDiff)}
                    />

                    <Field
                      info={'Soma das previsões que ainda não têm propostas fechadas.'}
                      title={translate('projects.projectOverview.open')}
                      value={formatCurrency(budgets.open)}
                    />
                  </DetailGroup>

                  <DetailGroup
                    description={'Pago, dívida e desvio face ao orçamento.'}
                    title={translate('projects.projectOverview.payments')}
                  >
                    <Field title={translate('common.labels.totalPaid')} value={formatCurrency(budgets.paid)} />

                    <Field
                      danger={budgets.debt.isGreaterThan(0)}
                      info={'Soma dos valores em dívida a fornecedores.'}
                      title={translate('common.labels.needPaid')}
                      value={formatCurrency(budgets.debt)}
                    />

                    <Field
                      danger={budgets.deviation.isLessThan(0)}
                      info={'Desvio face ao orçamento (apenas orçamentos com propostas).'}
                      title={translate('common.labels.budgetDeviation')}
                      value={formatCurrency(budgets.deviation)}
                    />

                    <Field
                      info={'Previsão inicial ajustada pelo desvio.'}
                      title={translate('common.labels.newPrevision')}
                      value={formatCurrency(budgets.newPrevision)}
                    />

                    <Field
                      info={'Quanto falta pagar até concluir.'}
                      title={translate('common.labels.toComplete')}
                      value={formatCurrency(budgets.toComplete)}
                    />
                  </DetailGroup>
                </div>

                {!!area && area.isGreaterThan(0) && (
                  <div className={'mt-6 border-t border-border pt-5'}>
                    <div className={'text-sm font-bold uppercase tracking-wide text-primary'}>
                      {translate('projects.projectOverview.statistics')}
                    </div>

                    <p className={'mb-3 mt-2 text-xs text-muted-foreground'}>
                      {`Preços por m² (área habitável: ${area.toString()} m²).`}
                    </p>

                    <div className={'grid grid-cols-2 gap-8 max-[991px]:grid-cols-1'}>
                      <Field
                        title={translate('projects.projectOverview.priceM2')}
                        value={formatCurrency(budgets.paid.dividedBy(area).toFixed(2))}
                      />

                      <Field
                        title={translate('projects.projectOverview.predictedPriceM2')}
                        value={formatCurrency(budgets.newPrevision.dividedBy(area).toFixed(2))}
                      />
                    </div>
                  </div>
                )}
              </Panel>

              {/* Per-budget breakdown + proposals */}
              <div className={'grid grid-cols-[1.4fr_1fr] gap-4 max-[1200px]:grid-cols-1'}>
                <Panel title={translate('projects.projectOverview.budgets')}>
                  {budgets.items.length === 0 ? (
                    <p className={'text-sm text-muted-foreground'}>{translate('projects.projectOverview.noBudgets')}</p>
                  ) : (
                    <div className={'space-y-3'}>
                      {budgets.items.map(item => {
                        const pct = item.allocated > 0 ? Math.min(100, (item.consumed / item.allocated) * 100) : 0;
                        const over = item.remaining < 0;

                        return (
                          <button
                            className={'block w-full text-left'}
                            key={item.id}
                            onClick={goToBudgets}
                            type={'button'}
                          >
                            <div className={'flex items-center justify-between gap-3 text-sm'}>
                              <span className={'truncate font-medium text-foreground'}>{item.name}</span>

                              <span className={'shrink-0 tabular-nums text-muted-foreground'}>
                                {formatCurrency(item.consumed)}
                                {' / '}
                                {formatCurrency(item.allocated)}
                              </span>
                            </div>

                            <div className={'mt-1.5 h-2 overflow-hidden rounded-full bg-muted'}>
                              <div
                                className={cn('h-full rounded-full', over ? 'bg-destructive' : 'bg-primary')}
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </Panel>

                <Panel title={translate('projects.projectOverview.proposals')}>
                  <div className={'space-y-3'}>
                    <div className={'flex items-baseline justify-between'}>
                      <span className={'text-sm text-muted-foreground'}>
                        {translate('projects.projectOverview.value')}
                      </span>

                      <span className={'text-lg font-semibold tabular-nums text-foreground'}>
                        {formatCurrency(overview.proposals.value)}
                      </span>
                    </div>

                    <div className={'grid grid-cols-3 gap-2 text-center'}>
                      <div className={'rounded-lg bg-muted/50 py-2'}>
                        <div className={'text-lg font-semibold tabular-nums text-foreground'}>
                          {overview.proposals.total}
                        </div>

                        <div className={'text-[11px] uppercase tracking-wide text-muted-foreground'}>
                          {translate('projects.projectOverview.total')}
                        </div>
                      </div>

                      <div className={'rounded-lg bg-muted/50 py-2'}>
                        <div className={'text-lg font-semibold tabular-nums text-primary'}>
                          {overview.proposals.accepted}
                        </div>

                        <div className={'text-[11px] uppercase tracking-wide text-muted-foreground'}>
                          {translate('projects.projectOverview.accepted')}
                        </div>
                      </div>

                      <div className={'rounded-lg bg-muted/50 py-2'}>
                        <div className={'text-lg font-semibold tabular-nums text-muted-foreground'}>
                          {overview.proposals.pending}
                        </div>

                        <div className={'text-[11px] uppercase tracking-wide text-muted-foreground'}>
                          {translate('projects.projectOverview.pending')}
                        </div>
                      </div>
                    </div>
                  </div>
                </Panel>
              </div>

              <Panel title={translate('projects.projectOverview.recentMovements')}>
                {overview.movements.recent.length === 0 ? (
                  <p className={'text-sm text-muted-foreground'}>{translate('projects.projectOverview.noMovements')}</p>
                ) : (
                  <div className={'divide-y divide-border'}>
                    {overview.movements.recent.map((movement: any) => (
                      <button
                        className={'flex w-full items-center justify-between gap-4 py-2.5 text-left text-sm'}
                        key={movement.id}
                        onClick={goToMovements}
                        type={'button'}
                      >
                        <div className={'min-w-0'}>
                          <div className={'truncate font-medium text-foreground'}>{movement.description}</div>

                          <div className={'text-xs text-muted-foreground'}>
                            {movement.date ? moment(movement.date).format('DD MMM YYYY') : ''}
                          </div>
                        </div>

                        <div className={'flex shrink-0 items-center gap-3'}>
                          <span
                            className={cn(
                              'rounded-full px-2 py-0.5 text-[11px] font-medium',
                              movement.paid ? 'bg-primary/10 text-primary' : 'bg-destructive/10 text-destructive'
                            )}
                          >
                            {translate(movement.paid ? 'common.labels.paid' : 'common.labels.needPaid')}
                          </span>

                          <span className={'tabular-nums text-foreground'}>{formatCurrency(movement.total)}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </Panel>
            </>
          )}

          <NoteLinksPanel entityId={projectId} entityType={'project'} />
        </div>
      )}
    </Loading>
  );
}
